import React, { useEffect, useState } from "react";
import { useGetFetchNew } from "../../data/Queries";
import { Col, Container, Row } from "react-bootstrap";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import Modal from "react-modal";
import { EditIcon, DeleteIcon } from "../../images/icons/website-icons";
import CommonStructured from "../common-components/CommonStructured";
import ManageInvestor from "./ManageInvestor";
import { formatDate } from "../../utils/utilityfunc";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import { showConfirmAlert } from "../components-react/ReactAlert";
import { delete_investor, get_investors } from "../../data/ApiEndpoints";
import FilterContainer from "../common-components/filters/filter-container";
import SearchFilter from "../common-components/filters/search-filter";
import SearchFilterWithButton from "../common-components/filters/search-filter-with-button";

const InvestorLanding = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(8);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modValues, setModValues] = useState({
    isModOpen: false,
    cardName: "",
  });
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { data, isLoading, refetch } = useGetFetchNew({
    token: userInfoData?.token,
    key: "investors",
    url: get_investors,
    filters: {
      limit: pageLimit,
      page: page + 1,
      search: searchQuery,
    },
  });

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModValues({
      isModOpen: false,
      cardName: "",
    });
  };

  const handleAddInvestor = () => {
    setInitialValues(null);
    setIsModalOpen(true);
    setModValues({
      isModOpen: true,
      cardName: "add",
    });
  };

  const handleEdit = (row) => {
    console.log("Editing investor:", row);
    console.log("Investor UID:", row?.uid);

    // Create a copy of row data to avoid reference issues
    const rowData = { ...row };

    // Ensure UID is present and valid
    if (!rowData.uid) {
      console.error("Warning: Investor data is missing UID!", rowData);
      toast.error("Cannot edit investor: Missing identifier");
      return;
    }

    console.log("Prepared investor data for edit:", rowData);
    setInitialValues(rowData);
    setIsModalOpen(true);
    setModValues({
      isModOpen: true,
      cardName: "edit",
    });
  };

  const handleDelete = async (uid) => {
    const handleConfirm = async () => {
      setLoading(true);
      try {
        console.log("Deleting investor with UID:", uid);
        const result = await dispatch(
          commonPostPutApi({
            method: "DELETE",
            endpoint: `${delete_investor}/${uid}`,
            token: userInfoData?.token,
          })
        );

        console.log("Delete response:", result);

        if (result?.payload?.status) {
          toast.success(
            result?.payload?.message || "Investor deleted successfully!"
          );
          refetch();
        } else {
          const errorMessage =
            result?.error?.message || result?.payload?.message;
          toast.error(errorMessage || "Failed to delete investor!");
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Something went wrong!");
      } finally {
        setLoading(false);
      }
    };

    showConfirmAlert(
      handleConfirm,
      "Are you sure you want to delete this investor?"
    );
  };

  const tbColumns = [
    {
      dataField: "name",
      text: "Investor Name",
      formatter: (cell, row) => (
        <span className="tb-data">{row?.name || "NA"}</span>
      ),
    },
    {
      dataField: "email",
      text: "email",
      formatter: (cell, row) => (
        <span className="tb-data" style={{ textTransform: "none" }}>
          {row?.email || "NA"}
        </span>
      ),
    },
    {
      dataField: "profile ",
      text: "Profile Image",
      formatter: (cell, row) => (
        <span className="tb-data">
          <img
            src={row?.profile || "NA"}
            alt="Profile"
            className="img-fluid"
            style={{ width: "50px", height: "50px" }}
          />
        </span>
      ),
    },
    {
      dataField: "investor_type",
      text: "Investor Type",
      formatter: (cell, row) => (
        <span className="tb-data">{row?.investor_type || "NA"}</span>
      ),
    },
    {
      dataField: "company_name",
      text: "Company Name",
      formatter: (cell, row) => (
        <span className="tb-data">{row?.company_name || "NA"}</span>
      ),
    },
    {
      dataField: "date_added",
      text: "Date Added",
      formatter: (cell, row) => (
        <span className="tb-data">{formatDate(row?.date_added || "NA")}</span>
      ),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell, row) => {
        return (
          <span className="tb-data flexCenter">
            <span
              className={
                row?.status === "active" ? "greenColorBg" : "redColorBg"
              }
            >
              {row?.status || "NA"}
            </span>
          </span>
        );
      },
    },
    {
      dataField: "seq_no",
      text: "Sequence",
      formatter: (cell, row) => (
        <span className="tb-data">{row?.seq_no || "NA"}</span>
      ),
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (cell, row) => {
        return (
          <div className="actionButtonContainer">
            <button
              className="actionBtn icon edit"
              onClick={() => handleEdit(row)}
              disabled={loading}
            >
              <EditIcon />
            </button>

            <button
              className="actionBtn icon delete"
              onClick={() => handleDelete(row?.uid)}
              disabled={loading}
            >
              {loading ? <span>...</span> : <DeleteIcon />}
            </button>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    refetch();
  }, [searchQuery]);

  const handleReset = () => {
    setSearchQuery("");
  };

  return (
    <Container className="bg-cloud-k px-50px gobal-container" fluid>
      <Row>
        <Col>
          <CommonStructured
            pageHeading={"Investors"}
            createBtnTxt={`+ Add Investor`}
            onClickCreateBtn={handleAddInvestor}
          />
        </Col>
      </Row>

      <FilterContainer onReset={handleReset} grid={2}>
        <SearchFilterWithButton
          selectedSearch={searchQuery}
          setSelectedSearch={(value) => setSearchQuery(value)}
          onSearch={(value) => {
            setSearchQuery(value);
          }}
        />
      </FilterContainer>

      <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt30">
        <PaymentLinkTable
          tableData={data?.data}
          tableColumns={tbColumns}
          userInfoData={userInfoData}
          isPage={page}
          setIsPage={(value) => setPage(value)}
          isLoading={isLoading}
          tablePagination={data?.pagination}
          setPageLimit={setPageLimit}
          pageLimit={pageLimit}
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onHide={handleModalClose}
        ariaHideApp={false}
        contentLabel="Investor Modal"
        onRequestClose={handleModalClose}
      >
        {isModalOpen && (
          <ManageInvestor
            isEdit={modValues.cardName === "edit"}
            closeModal={handleModalClose}
            token={userInfoData?.token}
            getInvestorData={refetch}
            initialValues={initialValues}
          />
        )}
      </Modal>
    </Container>
  );
};

export default InvestorLanding;
